import type { ProfileMention } from '@/src/features/posts/post-service';

export type ThreadableComment = {
  id: string;
  authorFullName: string | null;
  authorUid: string;
  authorUsername: string | null;
  createdAt?: unknown;
  localCreatedAtMillis?: number;
  parentCommentId?: string | null;
  reactionCount?: number | null;
  reactionCounts?: Record<string, number> | null;
  replyToCommentId?: string | null;
  updatedAt?: unknown;
};

export type CommentSortMode = 'recent' | 'oldest' | 'relevant';

export const DEFAULT_COMMENT_SORT_MODE: CommentSortMode = 'recent';

export type CommentReplyTarget = {
  parentCommentId: string;
  replyToCommentId: string;
  replyToFullName: string | null;
  replyToUid: string;
  replyToUsername: string | null;
};

export type ThreadedComment<T extends ThreadableComment> = {
  comment: T;
  replies: T[];
};

export type StableThreadedCommentOrderState = {
  replyIdsByThreadId: Record<string, string[]>;
  sortMode: CommentSortMode;
  threadIds: string[];
};

function timestampMillis(value: unknown) {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (
    value &&
    typeof value === 'object' &&
    'toMillis' in value &&
    typeof value.toMillis === 'function'
  ) {
    return value.toMillis();
  }

  if (typeof value === 'string') {
    const date = new Date(value);

    return Number.isFinite(date.getTime()) ? date.getTime() : 0;
  }

  return 0;
}

function commentTimeMillis(comment: ThreadableComment) {
  const createdAtMillis = timestampMillis(comment.createdAt);

  if (createdAtMillis > 0) {
    return createdAtMillis;
  }

  if (
    typeof comment.localCreatedAtMillis === 'number' &&
    Number.isFinite(comment.localCreatedAtMillis)
  ) {
    return comment.localCreatedAtMillis;
  }

  return 0;
}

function commentActivityMillis(comment: ThreadableComment) {
  return Math.max(
    commentTimeMillis(comment),
    timestampMillis(comment.updatedAt),
  );
}

function commentReactionCount(comment: ThreadableComment) {
  if (
    typeof comment.reactionCount === 'number' &&
    Number.isFinite(comment.reactionCount)
  ) {
    return Math.max(0, Math.floor(comment.reactionCount));
  }

  if (comment.reactionCounts && typeof comment.reactionCounts === 'object') {
    return Object.values(comment.reactionCounts).reduce(
      (total, count) =>
        total +
        (typeof count === 'number' && Number.isFinite(count) ? count : 0),
      0,
    );
  }

  return 0;
}

function oldestFirst<T extends ThreadableComment>(comments: T[]) {
  return [...comments].sort(
    (first, second) => commentTimeMillis(first) - commentTimeMillis(second),
  );
}

function sortReplies<T extends ThreadableComment>(
  comments: T[],
  sortMode: CommentSortMode,
) {
  if (sortMode === 'recent') {
    return [...comments].sort(
      (first, second) => commentTimeMillis(second) - commentTimeMillis(first),
    );
  }

  if (sortMode === 'relevant') {
    return [...comments].sort((first, second) => {
      const activityScore =
        commentReactionCount(second) - commentReactionCount(first);

      if (activityScore !== 0) {
        return activityScore;
      }

      return commentActivityMillis(second) - commentActivityMillis(first);
    });
  }

  return oldestFirst(comments);
}

function threadLatestMillis<T extends ThreadableComment>(
  thread: ThreadedComment<T>,
) {
  return [thread.comment, ...thread.replies].reduce(
    (latest, comment) => Math.max(latest, commentActivityMillis(comment)),
    0,
  );
}

function threadActivityScore<T extends ThreadableComment>(
  thread: ThreadedComment<T>,
) {
  const reactionScore = [thread.comment, ...thread.replies].reduce(
    (total, comment) => total + commentReactionCount(comment),
    0,
  );

  return thread.replies.length * 3 + reactionScore * 2;
}

function sortThreads<T extends ThreadableComment>(
  threads: ThreadedComment<T>[],
  sortMode: CommentSortMode,
) {
  if (sortMode === 'recent') {
    return [...threads].sort((first, second) => {
      const activitySort =
        threadLatestMillis(second) - threadLatestMillis(first);

      if (activitySort !== 0) {
        return activitySort;
      }

      return (
        commentTimeMillis(second.comment) - commentTimeMillis(first.comment)
      );
    });
  }

  if (sortMode === 'relevant') {
    return [...threads].sort((first, second) => {
      const relevanceSort =
        threadActivityScore(second) - threadActivityScore(first);

      if (relevanceSort !== 0) {
        return relevanceSort;
      }

      return threadLatestMillis(second) - threadLatestMillis(first);
    });
  }

  return [...threads].sort(
    (first, second) =>
      commentTimeMillis(first.comment) - commentTimeMillis(second.comment),
  );
}

export function displayNameForComment(comment: ThreadableComment) {
  return comment.authorFullName?.trim() || 'Frienzzi user';
}

export function replyTargetForComment(
  comment: ThreadableComment,
): CommentReplyTarget {
  return {
    parentCommentId: comment.parentCommentId || comment.id,
    replyToCommentId: comment.id,
    replyToFullName: comment.authorFullName,
    replyToUid: comment.authorUid,
    replyToUsername: comment.authorUsername,
  };
}

export function mentionForReplyTarget(
  target: CommentReplyTarget,
): ProfileMention {
  return {
    label: `@${target.replyToFullName?.trim() || 'Frienzzi user'}`,
    uid: target.replyToUid,
  };
}

export function composerStateForReply({
  body,
  mentions,
  target,
}: {
  body: string;
  mentions: ProfileMention[];
  target: CommentReplyTarget;
}) {
  const mention = mentionForReplyTarget(target);
  const trimmedBody = body.trimStart();
  const nextBody = trimmedBody.startsWith(mention.label)
    ? body
    : `${mention.label} ${trimmedBody}`;
  const nextMentions = [
    ...mentions.filter(
      (existing) =>
        existing.uid !== mention.uid || existing.label !== mention.label,
    ),
    mention,
  ];

  return {
    body: nextBody,
    cursor: nextBody.length,
    mentions: nextMentions,
  };
}

export function buildThreadedComments<T extends ThreadableComment>(
  comments: T[],
  sortMode: CommentSortMode = DEFAULT_COMMENT_SORT_MODE,
): ThreadedComment<T>[] {
  const commentIds = new Set(comments.map((comment) => comment.id));
  const repliesByParent = new Map<string, T[]>();
  const parents: T[] = [];

  comments.forEach((comment) => {
    const parentCommentId = comment.parentCommentId;

    if (parentCommentId && commentIds.has(parentCommentId)) {
      const replies = repliesByParent.get(parentCommentId) ?? [];

      replies.push(comment);
      repliesByParent.set(parentCommentId, replies);
      return;
    }

    parents.push(comment);
  });

  const threads = parents.map((comment) => {
    const replies = sortReplies(
      repliesByParent.get(comment.id) ?? [],
      sortMode,
    );
    const replyIds = new Set(replies.map((reply) => reply.id));
    const repliesByTarget = new Map<string, T[]>();
    const topLevelReplies: T[] = [];

    replies.forEach((reply) => {
      const replyToCommentId = reply.replyToCommentId;

      if (replyToCommentId && replyIds.has(replyToCommentId)) {
        const targetedReplies = repliesByTarget.get(replyToCommentId) ?? [];

        targetedReplies.push(reply);
        repliesByTarget.set(replyToCommentId, targetedReplies);
        return;
      }

      topLevelReplies.push(reply);
    });

    const orderedReplies: T[] = [];
    const visitedReplyIds = new Set<string>();
    const appendReply = (reply: T) => {
      if (visitedReplyIds.has(reply.id)) {
        return;
      }

      visitedReplyIds.add(reply.id);
      orderedReplies.push(reply);
      (repliesByTarget.get(reply.id) ?? []).forEach(appendReply);
    };

    topLevelReplies.forEach(appendReply);
    replies.forEach(appendReply);

    return {
      comment,
      replies: orderedReplies,
    };
  });

  return sortThreads(threads, sortMode);
}

function mergeStableCommentIds({
  nextSortedIds,
  previousIds,
  sortMode,
}: {
  nextSortedIds: string[];
  previousIds: string[];
  sortMode: CommentSortMode;
}) {
  const nextIdSet = new Set(nextSortedIds);
  const previousIdSet = new Set(previousIds);
  const existingIds = previousIds.filter((id) => nextIdSet.has(id));
  const newIds = nextSortedIds.filter((id) => !previousIdSet.has(id));

  if (sortMode !== 'recent') {
    return [...existingIds, ...newIds];
  }

  const result = [...existingIds];

  newIds.forEach((newId) => {
    const sortedIndex = nextSortedIds.indexOf(newId);
    const followingExistingId = nextSortedIds
      .slice(sortedIndex + 1)
      .find((id) => result.includes(id));

    if (!followingExistingId) {
      result.push(newId);
      return;
    }

    result.splice(result.indexOf(followingExistingId), 0, newId);
  });

  return result;
}

function orderStateFromThreads<T extends ThreadableComment>(
  threads: ThreadedComment<T>[],
  sortMode: CommentSortMode,
): StableThreadedCommentOrderState {
  return {
    replyIdsByThreadId: Object.fromEntries(
      threads.map((thread) => [
        thread.comment.id,
        thread.replies.map((reply) => reply.id),
      ]),
    ),
    sortMode,
    threadIds: threads.map((thread) => thread.comment.id),
  };
}

export function buildStableThreadedComments<T extends ThreadableComment>(
  comments: T[],
  sortMode: CommentSortMode = DEFAULT_COMMENT_SORT_MODE,
  previousOrderState?: StableThreadedCommentOrderState | null,
): {
  orderState: StableThreadedCommentOrderState;
  threads: ThreadedComment<T>[];
} {
  const sortedThreads = buildThreadedComments(comments, sortMode);

  if (!previousOrderState || previousOrderState.sortMode !== sortMode) {
    return {
      orderState: orderStateFromThreads(sortedThreads, sortMode),
      threads: sortedThreads,
    };
  }

  const sortedThreadsById = new Map(
    sortedThreads.map((thread) => [thread.comment.id, thread]),
  );
  const orderedThreadIds = mergeStableCommentIds({
    nextSortedIds: sortedThreads.map((thread) => thread.comment.id),
    previousIds: previousOrderState.threadIds,
    sortMode,
  });
  const threads = orderedThreadIds.map((threadId) => {
    const sortedThread = sortedThreadsById.get(threadId)!;
    const repliesById = new Map(
      sortedThread.replies.map((reply) => [reply.id, reply]),
    );
    const replyIds = mergeStableCommentIds({
      nextSortedIds: sortedThread.replies.map((reply) => reply.id),
      previousIds: previousOrderState.replyIdsByThreadId[threadId] ?? [],
      sortMode,
    });

    return {
      comment: sortedThread.comment,
      replies: replyIds.map((replyId) => repliesById.get(replyId)!),
    };
  });

  return {
    orderState: orderStateFromThreads(threads, sortMode),
    threads,
  };
}
